import React, { useEffect, useState, useRef } from 'react';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import api from '../api/client';
import { Depot } from '../types';
import { AddressItem, BR_ADDRESSES } from '../utils/mockState';
import { searchAddress } from '../utils/geocoding';

const TYPE_LABELS: Record<string, string> = {
  matriz: 'Matriz / Sede',
  distribuicao: 'Centro de Distribuição',
  crossdock: 'Cross-docking'
};

const TYPE_BADGE: Record<string, string> = {
  matriz: 'b-teal',
  distribuicao: 'b-gray',
  crossdock: 'b-amber'
};

export default function Depots() {
  const { showToast } = useToast();
  const [depots, setDepots] = useState<Depot[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal create/edit state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDepot, setEditingDepot] = useState<Depot | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [type, setType] = useState<'matriz' | 'distribuicao' | 'crossdock'>('distribuicao');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('São Paulo');
  const [uf, setUf] = useState('SP');
  const [latitude, setLatitude] = useState('-23.5615');
  const [longitude, setLongitude] = useState('-46.6560');

  // Address search and suggestions
  const [addressSuggestions, setAddressSuggestions] = useState<AddressItem[]>([]);
  const [showAddressSuggest, setShowAddressSuggest] = useState(false);
  const [activeAddressSuggestion, setActiveAddressSuggestion] = useState(-1);
  const addressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAddressSearch = (q: string) => {
    setAddress(q);
    setActiveAddressSuggestion(-1);
    if (!q.trim()) {
      setAddressSuggestions([]);
      setShowAddressSuggest(false);
      return;
    }

    // Procura localmente nos endereços de SP conhecidos primeiro
    const local = BR_ADDRESSES.filter((a) => a.label.toLowerCase().includes(q.toLowerCase()));
    if (local.length > 0) {
      setAddressSuggestions(local);
      setShowAddressSuggest(true);
    }

    if (addressTimer.current) clearTimeout(addressTimer.current);
    addressTimer.current = setTimeout(async () => {
      try {
        const remote = await searchAddress(q);
        if (remote.length > 0) {
          setAddressSuggestions(remote);
          setShowAddressSuggest(true);
        }
      } catch {
        // mantém sugestões locais se houver
      }
    }, 400);
  };

  const selectAddress = (addr: AddressItem) => {
    setAddress(addr.label);
    setCity(addr.city);
    setUf(addr.uf);
    setLatitude(String(addr.lat));
    setLongitude(String(addr.lng));
    setAddressSuggestions([]);
    setShowAddressSuggest(false);
    setActiveAddressSuggestion(-1);
  };

  const handleAddressKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setShowAddressSuggest(false);
      setActiveAddressSuggestion(-1);
      return;
    }
    if (!showAddressSuggest || addressSuggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveAddressSuggestion((index) => Math.min(index + 1, addressSuggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveAddressSuggestion((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeAddressSuggestion >= 0 && activeAddressSuggestion < addressSuggestions.length) {
      event.preventDefault();
      selectAddress(addressSuggestions[activeAddressSuggestion]);
    }
  };

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<Depot[]>('/depots');
      setDepots(data);
    } catch {
      showToast('Erro ao carregar depósitos.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateModal = () => {
    setEditingDepot(null);
    setName('');
    setType('distribuicao');
    setAddress('');
    setCity('São Paulo');
    setUf('SP');
    setLatitude('-23.5615');
    setLongitude('-46.6560');
    setAddressSuggestions([]);
    setShowAddressSuggest(false);
    setModalOpen(true);
  };

  const openEditModal = (d: Depot) => {
    setEditingDepot(d);
    setName(d.name);
    setType(d.type);
    setAddress(d.address);
    setCity(d.city);
    setUf(d.uf);
    setLatitude(String(d.latitude));
    setLongitude(String(d.longitude));
    setAddressSuggestions([]);
    setShowAddressSuggest(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('Preencha o nome do depósito.', 'error');
      return;
    }
    if (!address.trim()) {
      showToast('Preencha o endereço do depósito.', 'error');
      return;
    }
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) {
      showToast('Latitude e Longitude inválidas.', 'error');
      return;
    }

    setSaving(true);
    const payload = {
      name: name.trim(),
      type,
      address: address.trim(),
      city: city.trim(),
      uf: uf.trim().toUpperCase(),
      latitude: lat,
      longitude: lng
    };

    try {
      if (editingDepot) {
        await api.patch(`/depots/${editingDepot.id}`, payload);
        showToast(`Depósito "${name}" atualizado com sucesso!`, 'success');
      } else {
        await api.post('/depots', payload);
        showToast(`Depósito "${name}" cadastrado com sucesso!`, 'success');
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao salvar depósito.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d: Depot) => {
    if (!window.confirm(`Deseja realmente excluir o depósito "${d.name}"?`)) {
      return;
    }
    try {
      await api.delete(`/depots/${d.id}`);
      showToast(`Depósito "${d.name}" excluído.`, 'info');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao excluir depósito.', 'error');
    }
  };

  // Contadores de estatísticas
  const matrizCount = depots.filter((d) => d.type === 'matriz').length;
  const cdCount = depots.filter((d) => d.type === 'distribuicao').length;
  const crossdockCount = depots.filter((d) => d.type === 'crossdock').length;

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Depósitos & Centros de Distribuição</h1>
          <p className="page-lede">
            Bases logísticas e centros de distribuição que servem de ponto de partida e retorno para a frota.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openCreateModal}>
            + Novo Depósito
          </button>
        </div>
      </div>

      {/* 4 Premium Stat Cards */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat highlight" style={{ background: 'var(--teal-500-soft)', borderColor: 'var(--teal-500)' }}>
          <div className="lbl" style={{ color: 'var(--teal-800)' }}>
            <span className="ico" style={{ background: '#fff', color: 'var(--teal-800)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </span>
            Total de Bases
          </div>
          <div className="val" style={{ color: 'var(--teal-900)' }}>
            {depots.length}
            <span className="unit">unidades</span>
          </div>
          <div className="delta up">
            100% operacionais
          </div>
          <div className="foot">Origens e retornos cadastrados</div>
        </div>

        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18M6 21V9l6-5 6 5v12" />
              </svg>
            </span>
            Matrizes / Sedes
          </div>
          <div className="val">
            {matrizCount}
            <span className="unit">{matrizCount === 1 ? 'matriz' : 'matrizes'}</span>
          </div>
          <div className="delta flat">
            {depots.length > 0 ? Math.round((matrizCount / depots.length) * 100) : 0}% da rede
          </div>
          <div className="foot">Bases administrativas e centrais</div>
        </div>

        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </span>
            Centros de Distribuição
          </div>
          <div className="val">
            {cdCount}
            <span className="unit">CDs</span>
          </div>
          <div className="delta up">
            {depots.length > 0 ? Math.round((cdCount / depots.length) * 100) : 0}% da rede
          </div>
          <div className="foot">Armazenamento e separação regional</div>
        </div>

        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polyline>
              </svg>
            </span>
            Cross-dockings
          </div>
          <div className="val">
            {crossdockCount}
            <span className="unit">{crossdockCount === 1 ? 'ponto' : 'pontos'}</span>
          </div>
          <div className="delta flat">
            {depots.length > 0 ? Math.round((crossdockCount / depots.length) * 100) : 0}% da rede
          </div>
          <div className="foot">Transbordo e fluxo contínuo</div>
        </div>
      </div>

      {/* Depots Data Table */}
      <div className="tbl-wrap">
        <div style={{ overflowX: 'auto' }}>
        <table className="data">
          <thead>
            <tr>
              <th>Depósito / CD</th>
              <th>Tipo de Operação</th>
              <th>Endereço Completo</th>
              <th>Coordenadas</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {depots.map((d) => (
              <tr key={d.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: d.type === 'matriz' ? 'var(--teal-500-soft)' : 'var(--paper-2)',
                        color: d.type === 'matriz' ? 'var(--teal-800)' : 'var(--text-2)',
                        display: 'grid',
                        placeItems: 'center',
                        border: '1px solid var(--line-2)',
                        flexShrink: 0
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 21h18M6 21V9l6-5 6 5v12" />
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{d.city} · {d.uf}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`badge ${TYPE_BADGE[d.type] || 'b-gray'}`}>
                    <span className="dot" />
                    {TYPE_LABELS[d.type] || d.type}
                  </span>
                </td>
                <td style={{ color: 'var(--text-2)' }}>
                  {d.address}
                </td>
                <td className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {d.latitude.toFixed(4)}, {d.longitude.toFixed(4)}
                </td>
                <td>
                  <span className="badge b-teal">
                    <span className="dot" />
                    Ativo
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => openEditModal(d)}
                      title="Editar dados do depósito"
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(d)}
                      title="Excluir depósito"
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {depots.length === 0 && !loading && (
          <div className="empty">
            <h4>Nenhum depósito cadastrado</h4>
            <p>Clique no botão "+ Novo Depósito" acima para cadastrar a primeira base da sua frota.</p>
          </div>
        )}
      </div>

      {/* Create / Edit Depot Modal (Pop-up) */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingDepot ? `Editar Depósito: ${editingDepot.name}` : 'Cadastrar Novo Depósito'}
        maxWidth="620px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : editingDepot ? 'Salvar alterações' : 'Cadastrar depósito'}
            </button>
          </>
        }
      >
        <div>
          <div className="field-row">
            <div className="field">
              <label>Nome do Centro de Distribuição / Depósito</label>
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: CD Sumaré (Matriz)"
                autoFocus
              />
            </div>

            <div className="field">
              <label>Tipo de Operação</label>
              <select className="input" value={type} onChange={(e) => setType(e.target.value as any)}>
                <option value="distribuicao">Centro de Distribuição</option>
                <option value="matriz">Matriz / Sede Principal</option>
                <option value="crossdock">Base Cross-docking</option>
              </select>
            </div>
          </div>

          <div className="field" style={{ position: 'relative', marginTop: 12 }}>
            <label>Endereço Completo (com busca inteligente)</label>
            <input
              type="text"
              className="input"
              value={address}
              onChange={(e) => handleAddressSearch(e.target.value)}
              onKeyDown={handleAddressKeyDown}
              placeholder="Digite o endereço ou nome do local..."
              role="combobox"
              aria-expanded={showAddressSuggest}
              aria-controls="depot-address-suggestions"
              aria-activedescendant={activeAddressSuggestion >= 0 ? `depot-address-option-${activeAddressSuggestion}` : undefined}
            />

            {showAddressSuggest && addressSuggestions.length > 0 && (
              <div
                className="addr-suggest"
                id="depot-address-suggestions"
                role="listbox"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  maxHeight: 180,
                  overflowY: 'auto'
                }}
              >
                {addressSuggestions.map((a, idx) => (
                  <div
                    key={idx}
                    id={`depot-address-option-${idx}`}
                    className={`addr-suggest-item ${activeAddressSuggestion === idx ? 'keyboard-active' : ''}`}
                    role="option"
                    aria-selected={activeAddressSuggestion === idx}
                    onClick={() => selectAddress(a)}
                    style={{ padding: '8px 12px', cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{a.label}</div>
                    <div style={{ color: 'var(--text-3)', fontSize: 11.5 }}>
                      {a.city} / {a.uf}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Cidade</label>
              <input
                type="text"
                className="input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="São Paulo"
              />
            </div>

            <div className="field" style={{ maxWidth: 100 }}>
              <label>UF</label>
              <input
                type="text"
                className="input mono"
                maxLength={2}
                value={uf}
                onChange={(e) => setUf(e.target.value.toUpperCase())}
                placeholder="SP"
              />
            </div>
          </div>

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Latitude</label>
              <input
                type="number"
                step="any"
                className="input mono"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-23.5615"
              />
            </div>

            <div className="field">
              <label>Longitude</label>
              <input
                type="number"
                step="any"
                className="input mono"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-46.6560"
              />
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
